import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik } from 'formik'
import * as Yup from 'yup'

import { NotifyEmployeesForRenewalRequest, notifyEmployeesForRenewal } from 'core'

import { Button, DefaultDatePickerV2, Modal } from 'common/components'
import { addDaysV2, formatYearV2, nowAsString, nowV2 } from 'common/dateFormatters'
import { useCommandRequestV2 } from 'common/hooks'

import CancelRenewalBenefitNoticeModal from './cancelRenewalBenefitNoticeModal'
import ConfirmRenewalBenefitLaunchModal from './confirmRenewalBenefitLaunchModal'

type InviteIchraRenewalEmployeesFormProps = {
  visible: boolean
  benefitStartDate: string
  onCancel?: () => void
  onSendInvite?: () => void
}

type formData = {
  notificationDate?: string
}
const InviteIchraRenewalEmployeesModal = (props: InviteIchraRenewalEmployeesFormProps) => {
  const { t: translate } = useTranslation()

  const [isValidForm, setIsValidForm] = useState<boolean>()

  const [initialValues, setInitialValues] = useState<formData>({
    notificationDate: nowAsString(),
  })

  const [isVisibleConfirmModal, setIsVisibleConfirmModal] = useState<boolean>(false)
  const [isVisibleCancelRenewalNoticeModal, setIsVisibleCancelRenewalNoticeModal] = useState<boolean>(false)
  const maxNotificationDate = addDaysV2(props.benefitStartDate, -30)
  const year = formatYearV2(props.benefitStartDate)

  const { sendCommand, isLoading } = useCommandRequestV2((request: NotifyEmployeesForRenewalRequest) =>
    notifyEmployeesForRenewal(request),
  )

  useEffect(() => {
    setIsValidForm(formValidator(translate).isValidSync(initialValues))
  }, [])

  const handleConfirmModal = () => {
    setIsVisibleConfirmModal((prev) => !prev)
  }

  const onFormSubmit = async () => {
    const result = await sendCommand({ notificationDate: initialValues.notificationDate })
    if (result.completed) {
      if (props.onSendInvite) {
        props.onSendInvite()
      }
    }
  }

  return (
    <Modal
      className={'invite-ichra-renewal-employees-modal-wrap'}
      onCancel={() => {
        if (props.onCancel) {
          props.onCancel()
        }
      }}
      visible={props.visible}
      title={'Confirm your Benefit Notification'}
      footer={
        <div>
          <Button
            onClick={() => {
              if (props.onCancel) {
                props.onCancel()
              }
            }}
          >
            {translate('general.cancel')}
          </Button>

          <Button onClick={handleConfirmModal} type="primary" disabled={!isValidForm} className="ml-8">
            Send invite
          </Button>
        </div>
      }
    >
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={formValidator(translate)}
        onSubmit={() => onFormSubmit()}
        validate={(values) => {
          setIsValidForm(formValidator(translate).isValidSync(values))
          setInitialValues(values)
          return {}
        }}
      >
        {(formik) => (
          <>
            <div className="invite_employees_content">
              <p>PeopleKeep will send your employees an email informing them of their new {year} ICHRA benefit.</p>
            </div>

            <DefaultDatePickerV2
              name={'notificationDate'}
              label={'ICHRA Employee Notification Date'}
              disabledDate={(d) => !d || d.isBefore(nowV2(), 'day') || d.isSameOrAfter(maxNotificationDate, 'day')}
            />

            <ConfirmRenewalBenefitLaunchModal
              visible={isVisibleConfirmModal}
              onCancel={() => {
                setIsVisibleConfirmModal(false)
                setIsVisibleCancelRenewalNoticeModal(true)
              }}
              onSendInvite={async () => {
                await formik.submitForm()
              }}
              isLoading={isLoading}
            />

            <CancelRenewalBenefitNoticeModal
              visible={isVisibleCancelRenewalNoticeModal}
              onGoBack={() => {
                setIsVisibleCancelRenewalNoticeModal(false)
                setIsVisibleConfirmModal(true)
              }}
              onConfirmCancel={async () => {
                setIsVisibleCancelRenewalNoticeModal(false)
                setIsVisibleConfirmModal(false)

                /*
                 * This needs to be called in timeout otherwise it gives problem to the dispose of the previous 2 modals
                 * */
                const timeout = setTimeout(() => {
                  props.onCancel()
                  clearTimeout(timeout)
                }, 200)
              }}
            />
          </>
        )}
      </Formik>
    </Modal>
  )
}

export default InviteIchraRenewalEmployeesModal

const formValidator = (translate: any) =>
  Yup.object({
    notificationDate: Yup.string().required(translate('general.required')).nullable(),
  })
